import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { toast } from 'sonner';

export default function AdminLogin() {
  const navigate = useNavigate();
  // mode: null = loading, 'login' = existing account, 'register' = create first account
  const [mode, setMode] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Already authenticated (Google cookie or JWT token)? go to dashboard
    api.get('/auth/me')
      .then(() => navigate('/admin/dashboard'))
      .catch(() => {});
    api.get('/auth/status')
      .then((r) => setMode(r.data.initialized ? 'login' : 'register'))
      .catch(() => setMode('login'));
  }, [navigate]);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === 'register' ? '/auth/register' : '/auth/login';
      const res = await api.post(endpoint, { email, password });
      localStorage.setItem('le8_token', res.data.token);
      toast.success(mode === 'register' ? 'Compte administrateur créé' : 'Connexion réussie');
      navigate('/admin/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Une erreur est survenue';
      toast.error(detail);
      // If trying to register but an admin already exists, switch to login
      if (mode === 'register' && err.response?.status === 403) setMode('login');
    } finally {
      setLoading(false);
    }
  };

  const isRegister = mode === 'register';

  return (
    <div className="min-h-screen bg-[#1C2033] flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <h1 className="font-serif text-[#F7F2EC] text-4xl">Le 8<sup className="text-[0.6em]">e</sup> Sens</h1>
          <p className="eyebrow mt-3">Espace Administrateur</p>
        </div>

        {mode === null ? (
          <div className="bg-[#232845] border border-[#343a5c] rounded-2xl p-8 text-center">
            <p className="text-[#b7b2a8] font-sans">Chargement…</p>
          </div>
        ) : (
          <>
            <form onSubmit={submit} className="bg-[#232845] border border-[#343a5c] rounded-2xl p-8 space-y-6">
              <h2 className="font-serif text-[#F7F2EC] text-2xl text-center">
                {isRegister ? 'Créer votre compte admin' : 'Connexion'}
              </h2>
              {isRegister && (
                <p className="text-[#b7b2a8] text-sm text-center font-sans -mt-2">
                  Première utilisation : définissez vos identifiants administrateur.
                </p>
              )}
              <input
                className="line-input"
                type="email"
                data-testid="admin-login-email"
                inputMode="email"
                autoComplete="email"
                placeholder="Adresse e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                className="line-input"
                type="password"
                data-testid="admin-login-password"
                autoComplete={isRegister ? 'new-password' : 'current-password'}
                placeholder="Mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button data-testid="admin-login-submit" type="submit" disabled={loading} className="btn-gold w-full disabled:opacity-60">
                {loading ? 'Veuillez patienter…' : isRegister ? 'Créer le compte' : 'Se connecter'}
              </button>

              <div className="flex items-center gap-3 pt-1">
                <div className="h-px flex-1 bg-[#343a5c]" />
                <span className="text-[#8A8278] text-xs font-sans uppercase tracking-widest">ou</span>
                <div className="h-px flex-1 bg-[#343a5c]" />
              </div>

              <button
                type="button"
                onClick={googleLogin}
                data-testid="admin-login-google"
                className="w-full flex items-center justify-center gap-3 bg-white text-[#2A2823] rounded-full py-3.5 font-sans text-sm hover:bg-[#f0ece4] transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                Continuer avec Google
              </button>
            </form>

            <div className="text-center mt-6">
              <button
                type="button"
                onClick={() => setMode(isRegister ? 'login' : 'register')}
                data-testid="admin-login-mode"
                className="eyebrow text-[#C3A572] hover:text-[#F7F2EC] transition-colors"
              >
                {isRegister
                  ? 'Vous avez déjà un compte ? Se connecter'
                  : 'Première connexion ? Créer votre compte admin'}
              </button>
            </div>

            <div className="text-center mt-4">
              <a data-testid="admin-login-back" href="/" className="eyebrow text-[#8A8278] hover:text-[#C3A572] transition-colors">← Retour au site</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
